import { computed, Injectable, inject, signal } from '@angular/core';
import { DesktopDownloadService } from 'app/core/desktop/desktop-download.service';
import { isElectronApp, isLocalDevHost } from './pwa-env';
import {
    BeforeInstallPromptEvent,
    clearCapturedPwaInstallPrompt,
    getCapturedPwaInstallPrompt,
    PWA_INSTALL_READY_EVENT,
} from './pwa-install.prompt';

export type PwaInstallResult =
    | 'accepted'
    | 'dismissed'
    | 'ios'
    | 'unavailable'
    | 'reloading';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
    private readonly _desktop = inject(DesktopDownloadService);
    private _deferredPrompt: BeforeInstallPromptEvent | null = null;

    readonly canInstall = signal(false);
    readonly isInstalled = signal(false);
    readonly isIosInstallable = signal(false);
    /** Show install CTA: Electron download on desktop, or PWA prompt on mobile/Chromium. */
    readonly showInstallUi = computed(
        () =>
            !isElectronApp() &&
            (this._desktop.isConfigured() ||
                (!this.isInstalled() &&
                    (this.canInstall() ||
                        this.isIosInstallable() ||
                        this._isChromiumInstallCapable()))),
    );

    constructor() {
        if (typeof window === 'undefined') {
            return;
        }

        // Already running as the desktop app — hide PWA install UI.
        if (isElectronApp()) {
            this.isInstalled.set(true);
            return;
        }

        this._syncInstalledState();
        this._consumeCapturedPrompt();
        window.addEventListener(PWA_INSTALL_READY_EVENT, () =>
            this._consumeCapturedPrompt(),
        );
        window.addEventListener('beforeinstallprompt', (event) => {
            event.preventDefault();
            this._setDeferredPrompt(event as BeforeInstallPromptEvent);
        });
        window.addEventListener('appinstalled', () => {
            this._deferredPrompt = null;
            clearCapturedPwaInstallPrompt();
            this.canInstall.set(false);
            this._syncInstalledState();
            sessionStorage.removeItem('bizcare_pwa_sw_reload');
            sessionStorage.removeItem('bizcare_pwa_install_reload');
        });
        window
            .matchMedia('(display-mode: standalone)')
            .addEventListener('change', () => {
                this._syncInstalledState();
            });
    }

    init(): void {
        this._consumeCapturedPrompt();
        this._syncInstalledState();
        void this._waitForProductionServiceWorker();
    }

    async install(): Promise<PwaInstallResult> {
        this._consumeCapturedPrompt();

        if (this._deferredPrompt) {
            const prompt = this._deferredPrompt;
            await prompt.prompt();
            const { outcome } = await prompt.userChoice;
            this._deferredPrompt = null;
            clearCapturedPwaInstallPrompt();
            this.canInstall.set(false);
            if (outcome === 'accepted') {
                this._syncInstalledState();
            }
            return outcome;
        }

        if (this.isIosInstallable()) {
            return 'ios';
        }

        if (!sessionStorage.getItem('bizcare_pwa_install_reload')) {
            sessionStorage.setItem('bizcare_pwa_install_reload', '1');
            window.location.reload();
            return 'reloading';
        }

        return 'unavailable';
    }

    iosInstallHint(): string {
        return 'Tap the Share button in Safari, then choose "Add to Home Screen".';
    }

    desktopInstallHint(): string {
        if (isLocalDevHost() && !this._isLocalPwaDevEnabled()) {
            return 'To test desktop install on localhost, open the app with ?pwa=1 in the URL, then use the browser menu (⋮) → Install BizCare.';
        }

        return 'Open the browser menu (⋮) and choose "Install BizCare" or "Install app".';
    }

    private _isLocalPwaDevEnabled(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        return localStorage.getItem('bizcare_pwa_dev') === '1';
    }

    private async _waitForProductionServiceWorker(): Promise<void> {
        if (
            isLocalDevHost() ||
            !window.isSecureContext ||
            !('serviceWorker' in navigator)
        ) {
            return;
        }

        try {
            await navigator.serviceWorker.ready;
            this._consumeCapturedPrompt();

            if (
                !this._deferredPrompt &&
                !sessionStorage.getItem('bizcare_pwa_sw_reload')
            ) {
                sessionStorage.setItem('bizcare_pwa_sw_reload', '1');
                window.location.reload();
            }
        } catch {
            // Ignore.
        }
    }

    private _consumeCapturedPrompt(): void {
        const captured = getCapturedPwaInstallPrompt();
        if (captured) {
            this._setDeferredPrompt(captured);
        }
    }

    private _setDeferredPrompt(prompt: BeforeInstallPromptEvent): void {
        this._deferredPrompt = prompt;
        this.canInstall.set(true);
    }

    private _syncInstalledState(): void {
        const standalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as Navigator & { standalone?: boolean })
                .standalone === true;
        this.isInstalled.set(standalone);
        this.isIosInstallable.set(this._isIos() && !standalone);

        if (standalone) {
            this._deferredPrompt = null;
            clearCapturedPwaInstallPrompt();
            this.canInstall.set(false);
        }
    }

    private _isIos(): boolean {
        return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    }

    private _isChromiumInstallCapable(): boolean {
        const ua = window.navigator.userAgent;
        return (
            /Edg\//.test(ua) ||
            (/Chrome\//.test(ua) && !/Edg\//.test(ua)) ||
            /OPR\//.test(ua)
        );
    }
}
